import React, { useState, useEffect, useContext } from 'react';
import './styles/FullDetails.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { UserContext } from '../UserContext';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { FaDownload, FaTruck, FaMapMarkedAlt, FaClipboardList, FaCalendarAlt } from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { IoMdArrowBack } from "react-icons/io";
import { decryptId } from "../utils/encrypt";
import { openSecureFileUrl } from '../utils/fileAccess';

const formatDate = (date) => {
  if (!date) return 'NA';
  const temp = date.split('T')[0];
  return moment(temp).format('DD-MM-YYYY');
};

export default function CourierDetails() {
  const { user, token } = useContext(UserContext);
  const { requestId } = useParams();
  const realId = decryptId(requestId);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    bookingDetails: {},
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/requests/${realId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) navigate('/login');
          throw new Error("Unable to load request");
        }

        const data = await response.json();
        setFormData(data);

      } catch (err) {
        console.error("Error:", err.message);
      }
    };

    fetchData();
  }, [realId, token, navigate]);

  // FIX APPLIED HERE
  const finalSource =
    formData.fromLocation?.trim() ||
    formData.source?.trim() ||
    "NA";

  const finalDestination =
    formData.toLocation?.trim() ||
    formData.destination?.trim() ||
    "NA";
  // END FIX

  const generatePDF = () => {
    const doc = new jsPDF();
    let yOffset = 10;
    const margin = 10;
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const maxTextHeight = 10;
    const sectionHeaderHeight = 15;
    const labelMargin = 5;

    const calculateLabelWidth = (labels) => {
      let maxWidth = 0;
      labels.forEach(label => {
        const width = doc.getTextWidth(label);
        if (width > maxWidth) maxWidth = width;
      });
      return maxWidth;
    };

    const maxLabelWidth = calculateLabelWidth([
      'Request ID:', 'Support Needed:', 'Courier Type:', 'Office Location:',
      'From Address:', 'To Address:', 'Sender Name:', 'Receiver Name:',
      'Received Status:', 'Received On:', 'Requester Remarks', 'Status:',
      'Booking Date:', 'Booking By:', 'Amount:', 'POD Number:',
      'Courier Company', 'Booking Status:', 'Ticket Details:', 'Remarks:'
    ]) + labelMargin;

    const addText = (label, value, isBold = false) => {
      if (yOffset + maxTextHeight > pageHeight - margin) {
        doc.addPage();
        yOffset = margin;
      }

      doc.setFont("Helvetica", isBold ? "bold" : "normal");

      const labelText = `${label}:`;
      const valueX = margin + maxLabelWidth;
      const availableWidth = pageWidth - valueX - margin;

      doc.text(labelText, margin, yOffset);
      const valueLines = doc.splitTextToSize(value, availableWidth);

      valueLines.forEach(line => {
        if (yOffset + maxTextHeight > pageHeight - margin) {
          doc.addPage();
          yOffset = margin;
        }
        doc.text(line, valueX, yOffset);
        yOffset += maxTextHeight;
      });
    };

    const addSectionHeader = (header) => {
      yOffset += 5;
      if (yOffset + sectionHeaderHeight > pageHeight - margin) {
        doc.addPage();
        yOffset = margin;
      }
      addText(header, '', true);
    };

    const addSeparator = () => {
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yOffset, pageWidth - margin, yOffset);
      yOffset += 5;
    };

    doc.setFontSize(16);
    addText(`Request #${realId}`, '', true);
    addSeparator();
    doc.setFontSize(12);

    addSectionHeader("Courier Details");
    addText("Request ID", realId.toString());
    addText("Support Needed", formData.supportNeeded || 'NA');
    addText("Courier Type", formData.courierType || 'NA');
    addText("Office Location", formData.officeLocation || 'NA');
    addText("From Address", finalSource);
    addText("To Address", finalDestination);
    addText("Sender Name", formData.sentFrom || 'NA');
    addText("Receiver Name", formData.receivedBy || 'NA');
    addText("Received Status", formData.receivedStatus || 'NA');
    addText("Received On", formatDate(formData.receivedOn));
    addText("Status", formData.status || 'Pending');
    addText("Requester Remarks", formData.requesterRemarks || 'NA');

    if (formData.supportNeeded === 'Receive Courier') {
      addText("POD Number", formData.bookingDetails.podNumber || 'NA');
      addText("Courier Company", formData.bookingDetails.courierCo || 'NA');
    }

    if (formData.supportNeeded === 'Send Courier') {
      addSectionHeader("Booking Details");
      addText(
        "Booking Date",
        formData.bookingDetails.bookingDate
          ? formatDate(formData.bookingDetails.bookingDate)
          : 'NA'
      );
      addText("Booking By", formData.bookingDetails.bookingBy || 'NA');
      addText("Amount", formData.bookingDetails.amount || 'NA');
      addText("POD Number", formData.bookingDetails.podNumber || 'NA');
      addText("Courier Company", formData.bookingDetails.courierCo || 'NA');
      addText("Booking Status", formData.bookingDetails.bookingStatus || 'NA');
      addText("Ticket Details", formData.bookingDetails.fileName || 'NA');
    }

    doc.save(`Request#${realId}.pdf`);
  };

  const handleViewFile = async (event, fileUrl) => {
    event.preventDefault();
    try {
      await openSecureFileUrl(fileUrl, token);
    } catch (error) {
      console.error('Failed to open secure courier booking file:', error);
    }
  };

  return (
    <div className='Full-Details'>
      <Header />
      <main className='main-content'>
        <div className='download-container'>
          <button className='download-button' onClick={generatePDF}>
            <FaDownload />
            <span>Download as PDF</span>
          </button>
        </div>

        <Swiper
          modules={[Navigation, A11y]}
          slidesPerView={1}
          slidesPerGroup={1}
          spaceBetween={0}
          centeredSlides={false}
          loop={false}
          navigation
          watchOverflow
          className="Swiper"
        >
          <SwiperSlide>
            <div className="section-container">
              <div className="section-header">
                <div className="back-icon" onClick={() => navigate(-1)}>
                  <IoMdArrowBack />
                </div>
                Courier Details
              </div>

              <div className="section-body">
                <div className="detail-block">
                  <div className="detail-block__header">
                    <span className="detail-block__icon"><FaTruck /></span>
                    <div className="detail-block__title-group">
                      <h3 className="detail-block__title">Courier Information</h3>
                      <span className="detail-block__subtitle">Request and courier overview</span>
                    </div>
                  </div>
                  <div className="detail-block__body">
                    <div className="detail-row"><strong>Request ID</strong><span>{realId}</span></div>
                    <div className="detail-row"><strong>Support Needed</strong><span>{formData.supportNeeded || 'NA'}</span></div>
                    <div className="detail-row"><strong>Courier Type</strong><span>{formData.courierType || 'NA'}</span></div>
                    <div className="detail-row"><strong>Office Location</strong><span>{formData.officeLocation || 'NA'}</span></div>
                    <div className="detail-row"><strong>Sender Name</strong><span>{formData.sentFrom || 'NA'}</span></div>
                    <div className="detail-row"><strong>Receiver Name</strong><span>{formData.receivedBy || 'NA'}</span></div>
                    <div className="detail-row"><strong>Received Status</strong><span>{formData.receivedStatus || 'NA'}</span></div>
                    <div className="detail-row"><strong>Received On</strong><span>{formData.receivedOn ? formatDate(formData.receivedOn) : 'NA'}</span></div>
                    <div className="detail-row"><strong>Status</strong><span>{formData.status || 'Pending'}</span></div>
                    <div className="detail-row"><strong>Requester Remarks</strong><span>{formData.requesterRemarks || 'NA'}</span></div>
                  </div>
                </div>

                <div className="detail-block">
                  <div className="detail-block__header">
                    <span className="detail-block__icon"><FaMapMarkedAlt /></span>
                    <div className="detail-block__title-group">
                      <h3 className="detail-block__title">Address Details</h3>
                      <span className="detail-block__subtitle">Courier pickup and delivery locations</span>
                    </div>
                  </div>
                  <div className="detail-block__body">
                    <div className="detail-row"><strong>From Address</strong><span>{finalSource}</span></div>
                    <div className="detail-row"><strong>To Address</strong><span>{finalDestination}</span></div>
                  </div>
                </div>

                {formData.supportNeeded === 'Receive Courier' && (
                  <div className="detail-block">
                    <div className="detail-block__header">
                      <span className="detail-block__icon"><FaClipboardList /></span>
                      <div className="detail-block__title-group">
                        <h3 className="detail-block__title">Delivery Details</h3>
                        <span className="detail-block__subtitle">Courier receipt information</span>
                      </div>
                    </div>
                    <div className="detail-block__body">
                      <div className="detail-row"><strong>POD Number</strong><span>{formData.bookingDetails.podNumber || 'NA'}</span></div>
                      <div className="detail-row"><strong>Courier Company</strong><span>{formData.bookingDetails.courierCo || 'NA'}</span></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </SwiperSlide>

          {formData.supportNeeded === 'Send Courier' && (
            <SwiperSlide>
              <div className="section-container">
                <div className="section-header">
                  <div className="back-icon" onClick={() => navigate(-1)}>
                    <IoMdArrowBack />
                  </div>
                  Booking Details
                </div>

                <div className="section-body">
                  <div className="detail-block">
                    <div className="detail-block__header">
                      <span className="detail-block__icon"><FaCalendarAlt /></span>
                      <div className="detail-block__title-group">
                        <h3 className="detail-block__title">Booking Information</h3>
                        <span className="detail-block__subtitle">Courier booking and tracking details</span>
                      </div>
                    </div>
                    <div className="detail-block__body">
                      <div className="detail-row">
                        <strong>Booking Date</strong>
                        <span>{formData.bookingDetails.bookingDate ? formatDate(formData.bookingDetails.bookingDate) : 'NA'}</span>
                      </div>
                      <div className="detail-row"><strong>Booking By</strong><span>{formData.bookingDetails.bookingBy || 'NA'}</span></div>
                      <div className="detail-row"><strong>Amount</strong><span>{formData.bookingDetails.amount || 'NA'}</span></div>
                      <div className="detail-row"><strong>POD Number</strong><span>{formData.bookingDetails.podNumber || 'NA'}</span></div>
                      <div className="detail-row"><strong>Courier Company</strong><span>{formData.bookingDetails.courierCo || 'NA'}</span></div>
                      <div className="detail-row"><strong>Booking Status</strong><span>{formData.bookingDetails.bookingStatus || 'NA'}</span></div>
                      <div className="detail-row">
                        <strong>Ticket Details</strong>
                        <span>
                          {formData.bookingDetails.fileUrl ? (
                            <a
                              href={formData.bookingDetails.fileUrl}
                              className="view-download"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => handleViewFile(e, formData.bookingDetails.fileUrl)}
                            >
                              {formData.bookingDetails.fileName}
                            </a>
                          ) : 'NA'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          )}
        </Swiper>
      </main>
      <Footer />
    </div>
  );
}
